import { Alert, App as AntApp, DatePicker, Form, Input, InputNumber, Modal, Select } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { toAmount } from "../app/balance.ts";
import { billingKeys, useServiceKinds, type ServiceKind, type ServicePrice } from "../app/billing.ts";

const kindNameMaxLength = 200;

interface PriceForm {
  serviceKindId?: string;
  amount?: number;
  validFrom?: Dayjs;
}

/** Adds a price version for a service kind from a date, or corrects the amount of an unused version. */
export function ServicePriceModal({ target, onClose }: { target: ServicePrice | "new" | undefined; onClose(): void }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const kinds = useServiceKinds();
  const [form] = Form.useForm<PriceForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const existing = target !== undefined && target !== "new" ? target : undefined;

  useEffect(() => {
    if (target === undefined) return;
    form.resetFields();
    if (existing) form.setFieldsValue({ amount: toAmount(existing.amount) });
    setSummary([]);
  }, [target, existing, form]);

  const save = useMutation({
    mutationFn: async (values: PriceForm) => {
      const result = existing
        ? await api.PUT("/billing/service-prices/{id}", { params: { path: { id: existing.id } }, body: { amount: values.amount ?? null } })
        : await api.POST("/billing/service-prices", {
            body: { serviceKindId: values.serviceKindId ?? null, amount: values.amount ?? null, validFrom: values.validFrom?.format("YYYY-MM-DD") ?? null },
          });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: billingKeys.all }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
      void message.success(t(existing ? "billing.saved" : "billing.versionAdded"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const required = [{ required: true, message: t("common.required") }];
  return (
    <Modal
      open={target !== undefined}
      title={existing ? t("billing.services.editTitle", { name: existing.serviceKindName }) : t("billing.services.createTitle")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={existing ? t("billing.editHint") : t("billing.newVersionHint")} />
      <Form<PriceForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => save.mutate(values)}>
        {!existing && (
          <Form.Item name="serviceKindId" label={t("billing.services.kind")} rules={required} extra={t("billing.services.kindHint")}>
            <Select
              loading={kinds.isPending}
              showSearch={{ optionFilterProp: "label" }}
              options={(kinds.data ?? []).map((k) => ({ value: k.id, label: k.name }))}
              notFoundContent={t("billing.services.noKinds")}
            />
          </Form.Item>
        )}
        <Form.Item name="amount" label={t("billing.services.amount")} rules={required}>
          <InputNumber<number> style={{ width: "100%" }} min={0} precision={2} step={10000} />
        </Form.Item>
        {!existing && (
          <Form.Item name="validFrom" label={t("billing.validFrom")} rules={required} extra={t("billing.validFromHint")}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

/** Creates or renames a service kind; the list of services is entered by the school, not fixed in code. */
export function ServiceKindModal({ target, onClose }: { target: ServiceKind | "new" | undefined; onClose(): void }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ name?: string }>();
  const [summary, setSummary] = useState<string[]>([]);
  const existing = target !== undefined && target !== "new" ? target : undefined;

  useEffect(() => {
    if (target === undefined) return;
    form.resetFields();
    form.setFieldsValue({ name: existing?.name ?? "" });
    setSummary([]);
  }, [target, existing, form]);

  const save = useMutation({
    mutationFn: async (name: string) => {
      const result = existing
        ? await api.PUT("/billing/service-kinds/{id}", { params: { path: { id: existing.id } }, body: { name } })
        : await api.POST("/billing/service-kinds", { body: { name } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: billingKeys.all }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
      void message.success(t("billing.services.kindSaved"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  return (
    <Modal
      open={target !== undefined}
      title={existing ? t("billing.services.renameKindTitle") : t("billing.services.createKindTitle")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(values) => save.mutate((values.name ?? "").trim())}>
        <Form.Item name="name" label={t("billing.services.kindName")} rules={[{ required: true, whitespace: true, message: t("common.required") }]}>
          <Input maxLength={kindNameMaxLength} autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}
